import { Code2, Gamepad2, Home, LogOut, Megaphone, MessageCircle, Plus, Settings, ShieldCheck, Users } from 'lucide-react';
import clsx from 'clsx';
import type { Profile } from '../types';
import Avatar from './Avatar';

export type RailPanel = 'chat' | 'friends' | 'admin';

export default function SidebarRail({
  activePanel,
  onPanelChange,
  profile,
  onSignOut,
  onOpenSettings,
}: {
  activePanel: RailPanel;
  onPanelChange: (panel: RailPanel) => void;
  profile: Profile;
  onSignOut: () => void;
  onOpenSettings: () => void;
}) {
  const items = [
    { id: 'chat' as const, label: 'Messages', icon: MessageCircle },
    { id: 'friends' as const, label: 'Friends', icon: Users },
    { id: 'admin' as const, label: 'Admin', icon: ShieldCheck, disabled: profile.role !== 'admin' },
  ];
  const spaces = [
    { label: 'Home', short: 'R', icon: Home, active: true },
    { label: 'Design Hub', short: 'DH' },
    { label: 'Product Room', short: 'PR' },
    { label: 'Developers', short: 'DV', icon: Code2 },
    { label: 'Gaming', short: 'GG', icon: Gamepad2 },
    { label: 'Announcements', short: 'AN', icon: Megaphone },
  ];

  return (
    <aside className="relay-glass flex h-screen w-16 shrink-0 flex-col items-center border-r border-white/10 py-3 md:w-[76px]">
      <nav className="relay-scrollbar flex min-h-0 flex-1 flex-col items-center gap-2 overflow-y-auto px-2" aria-label="Communities">
        {spaces.map(({ label, short, icon: Icon, active }) => (
          <button
            key={label}
            type="button"
            title={label}
            className={clsx(
              'group relative grid h-12 w-12 place-items-center rounded-2xl border text-sm font-black tracking-tight transition duration-200 hover:rounded-xl',
              active
                ? 'border-relay-cyan/45 bg-gradient-to-br from-relay-blue to-relay-violet text-white shadow-glow'
                : 'border-white/10 bg-white/[0.045] text-slate-300 hover:border-white/20 hover:bg-white/[0.08] hover:text-white',
            )}
          >
            {active && <span className="absolute -left-2 h-7 w-1 rounded-r-full bg-relay-cyan" />}
            {Icon ? <Icon size={20} /> : short}
          </button>
        ))}
        <button
          type="button"
          title="Add community"
          className="grid h-12 w-12 place-items-center rounded-2xl border border-dashed border-white/15 text-slate-500 transition hover:rounded-xl hover:border-relay-cyan/40 hover:text-relay-cyan"
        >
          <Plus size={19} />
        </button>
      </nav>

      <div className="my-3 h-px w-9 bg-white/10" />

      <nav className="flex flex-col gap-2" aria-label="Primary navigation">
        {items.map(({ id, label, icon: Icon, disabled }) => (
          <button
            key={id}
            type="button"
            onClick={() => onPanelChange(id)}
            disabled={disabled}
            title={disabled ? 'Admin profile required' : label}
            className={clsx(
              'grid h-11 w-11 place-items-center rounded-xl border text-slate-400 transition',
              activePanel === id
                ? 'border-relay-cyan/40 bg-relay-cyan/15 text-relay-cyan shadow-glow'
                : 'border-white/10 bg-white/[0.03] hover:border-white/20 hover:text-white',
              disabled && 'cursor-not-allowed opacity-40 hover:text-slate-400',
            )}
            aria-label={label}
          >
            <Icon size={20} />
          </button>
        ))}
      </nav>

      <button
        type="button"
        onClick={onOpenSettings}
        className="mt-3 grid h-11 w-11 place-items-center rounded-xl border border-white/10 bg-white/[0.03] text-slate-400 transition hover:border-white/20 hover:text-white"
        aria-label="Open settings"
        title="Settings"
      >
        <Settings size={20} />
      </button>

      <button
        type="button"
        onClick={onOpenSettings}
        className="mt-3 rounded-xl transition hover:scale-105"
        aria-label="Open account settings"
        title={profile.display_name}
      >
        <Avatar name={profile.display_name} src={profile.avatar_url} online size="sm" />
      </button>

      <button
        type="button"
        onClick={onSignOut}
        className="mt-2 grid h-11 w-11 place-items-center rounded-xl border border-white/10 bg-white/[0.03] text-slate-400 transition hover:border-relay-red/40 hover:text-relay-red"
        aria-label="Sign out"
        title="Sign out"
      >
        <LogOut size={20} />
      </button>
    </aside>
  );
}
